/**
 * The business switcher at the top of the CenoX Business sidebar.
 *
 * Always opens, even for somebody with one business: it lists every business
 * this account belongs to, ticks the one in use, and — for a personal
 * account — ends with Add Business, so a second business is reachable from
 * the first without signing out.
 *
 * Switching is `selectBusiness`: every query is keyed by business, so each
 * screen refetches for the one chosen. Nothing is shared between businesses;
 * the API scopes every request to the business in its path.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Building2, Check, ChevronDown, Plus } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { cn } from './ui';
import { AddBusinessDialog } from './AddBusinessDialog';

export function BusinessSwitcher(): ReactNode {
  const { user, business, memberships, selectBusiness } = useAuth();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [addBusinessOpen, setAddBusinessOpen] = useState(false);
  const switcherBox = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  /**
   * The API's own rule: a person may create a business, a shared location
   * login may not. The server refuses it either way; this only avoids showing
   * a button that would fail.
   */
  const mayAddBusiness = user?.kind === 'personal';

  useEffect(() => setSwitcherOpen(false), [pathname]);

  // A click anywhere else closes the list.
  useEffect(() => {
    if (!switcherOpen) return;
    const away = (e: MouseEvent): void => {
      if (!switcherBox.current?.contains(e.target as Node)) setSwitcherOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [switcherOpen]);

  return (
    <div className="relative" ref={switcherBox}>
      <button
        type="button"
        onClick={() => setSwitcherOpen((open) => !open)}
        aria-expanded={switcherOpen}
        aria-haspopup="listbox"
        aria-label={`${business?.businessName ?? 'Business'}: switch business`}
        className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-slate-50"
      >
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
          <Building2 className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-900">
            {business?.businessName}
          </p>
          <p className="truncate text-xs text-slate-500 capitalize">
            {business?.role} · {business?.currencyCode}
          </p>
        </div>
        <ChevronDown
          className={cn('size-4 shrink-0 text-slate-400 transition-transform', switcherOpen && 'rotate-180')}
          aria-hidden
        />
      </button>

      {switcherOpen && (
        <div className="absolute inset-x-0 top-full z-20 mt-1 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
          <ul role="listbox" aria-label="Your businesses" className="max-h-72 space-y-0.5 overflow-y-auto">
            {memberships.map((membership) => {
              const current = membership.businessId === business?.businessId;
              return (
                <li key={membership.businessId}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={current}
                    onClick={() => {
                      setSwitcherOpen(false);
                      if (!current) selectBusiness(membership.businessId);
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm',
                      current ? 'bg-brand-50 font-medium text-brand-700' : 'text-slate-600 hover:bg-slate-50',
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{membership.businessName}</span>
                      <span className="block truncate text-xs font-normal capitalize text-slate-400">
                        {membership.role}
                      </span>
                    </span>
                    {current && <Check className="size-4 shrink-0 text-brand-600" aria-label="Current business" />}
                  </button>
                </li>
              );
            })}
          </ul>
          {mayAddBusiness && (
            <button
              type="button"
              onClick={() => {
                setSwitcherOpen(false);
                setAddBusinessOpen(true);
              }}
              className="mt-1 flex w-full items-center gap-2 rounded-lg border-t border-slate-100 px-2.5 py-2 text-left text-sm font-medium text-brand-700 hover:bg-slate-50"
            >
              <Plus className="size-4 shrink-0" aria-hidden />
              Add Business
            </button>
          )}
        </div>
      )}
      <AddBusinessDialog open={addBusinessOpen} onClose={() => setAddBusinessOpen(false)} />
    </div>
  );
}
